import React, { useEffect, useRef, useState } from "react";
import { Input as AntInput } from "antd";
import { Spin } from "./Spin";

type FetchResult<T> = { items: T[]; total?: number };

export type SearchSelectProps<T> = {
  placeholder?: string;
  minLength?: number;
  debounceMs?: number;
  pageSize?: number;
  className?: string;
  fetcher: (
    query: string,
    page?: number,
    pageSize?: number,
  ) => Promise<FetchResult<T>>;
  onSelect: (item: T) => void;
  renderItem?: (item: T) => React.ReactNode;
  getItemKey?: (item: T) => string;
};

export function SearchSelect<T>({
  placeholder,
  minLength = 2,
  debounceMs = 300,
  pageSize = 10,
  className = "",
  fetcher,
  onSelect,
  renderItem,
  getItemKey,
}: SearchSelectProps<T>) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // close on outside click
    function onClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    if (!query || query.length < minLength) {
      setItems([]);
      setLoading(false);
      return;
    }

    timerRef.current = window.setTimeout(() => {
      setLoading(true);
      fetcher(query, 1, pageSize)
        .then((r) => {
          setItems(r.items || []);
          setShow(true);
          setHighlighted(0);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, debounceMs);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [query, fetcher, debounceMs, minLength, pageSize]);

  function handleSelect(item: T) {
    const label =
      (item as any)?.name ??
      (item as any)?.label ??
      (getItemKey ? getItemKey(item) : ((item as any)?.id ?? ""));
    setQuery(String(label));
    setShow(false);
    onSelect(item);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!show) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[highlighted];
      if (item) handleSelect(item);
    } else if (e.key === "Escape") {
      setShow(false);
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <AntInput.Search
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (items.length > 0) setShow(true);
        }}
        onKeyDown={onKeyDown}
      />

      {show && (
        <div className="absolute left-0 right-0 mt-2 z-50 bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-auto">
          {loading ? (
            <div className="p-4 flex items-center justify-center">
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">
              No se encontraron resultados
            </div>
          ) : (
            items.map((item, idx) => {
              const key = getItemKey
                ? getItemKey(item)
                : ((item as any)?.id ?? idx);
              return (
                <div
                  key={String(key)}
                  role="option"
                  aria-selected={highlighted === idx}
                  onMouseDown={(ev) => {
                    // prevent blur before click
                    ev.preventDefault();
                    handleSelect(item);
                  }}
                  onMouseEnter={() => setHighlighted(idx)}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${highlighted === idx ? "bg-gray-100" : ""}`}
                >
                  {renderItem
                    ? renderItem(item)
                    : ((item as any)?.name ?? String(key))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
